import api from './api';
import { Festejo, FestojoFormData, FestojoStatus } from '../types/festejo';

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface FestejosListResponse {
  success: boolean;
  data: Festejo[];
  pagination: PaginationInfo;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

class FestojoService {
  private baseURL = '/festejos';

  // Listar festejos
  async getFestejos(params: Record<string, string | number> = {}): Promise<FestejosListResponse> {
    const response = await api.get<FestejosListResponse>(this.baseURL, { params });
    return response.data;
  }

  async getBySlug(slug: string): Promise<Festejo> {
    try {
      const response = await api.get<ApiResponse<Festejo>>(`/festejos/slug/${slug}`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Festejo não encontrado');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Festejo não encontrado');
      }
      throw new Error(error.response?.data?.message || 'Erro ao buscar festejo');
    }
  }

  // Buscar festejo por slug
  async getFestojoBySlug(slug: string): Promise<ApiResponse<Festejo>> {
    const response = await api.get<ApiResponse<Festejo>>(`${this.baseURL}/${slug}`);
    return response.data;
  }

  // Criar novo festejo
  async createFestejo(festojoData: FestojoFormData): Promise<ApiResponse<Festejo>> {
    const response = await api.post<ApiResponse<Festejo>>(this.baseURL, festojoData);
    return response.data;
  }

  // Atualizar festejo
  async updateFestejo(id: string, festojoData: FestojoFormData): Promise<ApiResponse<Festejo>> {
    const response = await api.put<ApiResponse<Festejo>>(`${this.baseURL}/${id}`, festojoData);
    return response.data;
  }

  // Excluir festejo
  async deleteFestejo(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${this.baseURL}/${id}`);
    return response.data;
  }

  // Upload de imagem de capa
  async uploadCoverImage(
    festojoId: string,
    imageFile: File
  ): Promise<ApiResponse<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append('coverImage', imageFile);

    const response = await api.post<ApiResponse<{ imageUrl: string }>>(
      `${this.baseURL}/${festojoId}/upload-cover`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Upload de imagem para seção
  async uploadSectionImage(
    festojoId: string,
    imageFile: File
  ): Promise<ApiResponse<{ imageUrl: string; filename: string }>> {
    const formData = new FormData();
    formData.append('sectionImage', imageFile);

    const response = await api.post<ApiResponse<{ imageUrl: string; filename: string }>>(
      `${this.baseURL}/${festojoId}/upload-section-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // Atualizar status do festejo
  async updateStatus(
    id: string,
    status: FestojoStatus
  ): Promise<ApiResponse<{ status: FestojoStatus }>> {
    const response = await api.put<ApiResponse<{ status: FestojoStatus }>>(
      `${this.baseURL}/${id}/status`,
      { status }
    );
    return response.data;
  }

  // Verificar disponibilidade de slug
  async checkSlugAvailability(slug: string): Promise<ApiResponse<{ available: boolean; message: string }>> {
    const response = await api.get<ApiResponse<{ available: boolean; message: string }>>(
      `${this.baseURL}/check-slug/${slug}`
    );
    return response.data;
  }

  // Salvar rascunho
  async saveDraft(festojoData: FestojoFormData): Promise<ApiResponse<Festejo>> {
    const draftData = { ...festojoData, status: 'draft' as FestojoStatus };

    if (festojoData.id) {
      return this.updateFestejo(festojoData.id, draftData);
    } else {
      return this.createFestejo(draftData);
    }
  }

  // Publicar festejo
  // Publicar festejo
  async publishFestejo(festojoData: FestojoFormData): Promise<ApiResponse<Festejo>> {
    const publishData = { ...festojoData, status: 'published' as FestojoStatus };

    console.log('📦 Dados que serão enviados para publicação do festejo:', publishData);

    if (festojoData.id) {
      return this.updateFestejo(festojoData.id, publishData);
    } else {
      return this.createFestejo(publishData);
    }
  } 

  // Validação dos dados (client-side)
  validateFestojoData(data: Partial<FestojoFormData>): ValidationResult {
    const errors: string[] = [];

    if (!data.title || data.title.trim().length < 3) {
      errors.push('Título deve ter pelo menos 3 caracteres');
    }

    if (!data.slug || data.slug.trim().length < 3) {
      errors.push('Slug deve ter pelo menos 3 caracteres');
    }

    if (!data.padroeiro || data.padroeiro.trim().length < 2) {
      errors.push('Nome do padroeiro é obrigatório');
    }

    if (!data.description || data.description.trim().length < 10) {
      errors.push('Descrição deve ter pelo menos 10 caracteres');
    }

    if (!data.paroquia || data.paroquia.trim().length < 3) {
      errors.push('Nome da paróquia é obrigatório');
    }

    if (!data.endereco || data.endereco.trim().length < 5) {
      errors.push('Endereço é obrigatório');
    }

    if (!data.cidade || data.cidade.trim().length < 2) {
      errors.push('Cidade é obrigatória');
    }

    if (!data.estado || data.estado.trim().length !== 2) {
      errors.push('Estado deve ter 2 caracteres');
    }

    if (!data.contactEmail || !/\S+@\S+\.\S+/.test(data.contactEmail)) {
      errors.push('Email de contato válido é obrigatório');
    }

    if (!data.contactPhone || data.contactPhone.trim().length < 10) {
      errors.push('Telefone de contato é obrigatório');
    }

    if (!data.startDate) {
      errors.push('Data de início é obrigatória');
    }

    if (!data.endDate) {
      errors.push('Data de término é obrigatória');
    }

    if (data.startDate && data.endDate && new Date(data.startDate) >= new Date(data.endDate)) {
      errors.push('Data de término deve ser posterior à data de início');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

const festojoService = new FestojoService();
export default festojoService;
export type { ApiResponse, FestejosListResponse, ValidationResult };
